<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { upperCase } from 'lodash'

import { EventGroup } from '@/types/delivery-management-dispatcher-overview-widget'
import { TourEventStatus } from '@/types/delivery-management'
import { differenceFromMinutes, localeTime } from '@/utils/dates'

import ShipmentTypeBadge from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/ShipmentTypeBadge.vue'

interface Props {
    eventGroup: EventGroup
    isTourStarted: boolean
    index: number
    eventGroupsCount: number
}

defineProps<Props>()

const { t } = useI18n()

function calculateExpectedProducts(eventGroup: EventGroup): number {
    if (!eventGroup.shipment || !eventGroup.shipment?.products) return 0

    return eventGroup.shipment.products
        .map((product) => product.delivered ?? 0)
        .reduce((a, b) => a + b, 0)
}
</script>

<template>
    <div
        class="relative h-full px-3 py-3.5 flex flex-col self-stretch rounded-none overflow-hidden bg-clip-padding text-white"
        :class="{
            'bg-green-500  hover:bg-green-600': eventGroup.status === TourEventStatus.Done,
            'bg-secondary-400 hover:bg-secondary-500':
                eventGroup.status === TourEventStatus.Current,
            'bg-primary-200 text-stone-800! dark:bg-dark-400 dark:text-stone-100! dark:hover:bg-dark-300 hover:bg-primary-300':
                eventGroup.status === TourEventStatus.Planned,
            'rounded-r-lg': index === eventGroupsCount - 1,
        }"
    >
        <div class="flex items-start justify-between gap-2">
            <span class="text-sm py-1 font-medium truncate">
                {{ eventGroup.address }} - {{ eventGroup.zipcodeCity }}
                <br />
                {{ eventGroup.shipmentCustomers }}
            </span>

            <ShipmentTypeBadge
                v-if="eventGroup.shipment"
                :shipment-type="eventGroup.shipment.type"
            />
        </div>

        <div v-if="eventGroup.status === TourEventStatus.Planned" class="text-[11px]">
            <span class="uppercase opacity-80 text-[12px]" v-text="t('planned') + ' '" />
            <span class="font-semibold" v-text="eventGroup.plannedAt" />
            <template v-if="isTourStarted && eventGroup.eta">
                <span class="opacity-80" v-text="' - '" />
                <span
                    v-tooltip="t('etaExplanation')"
                    class="inline-flex items-baseline gap-1 whitespace-nowrap align-baseline"
                >
                    <span
                        class="uppercase opacity-80 text-yellow-500"
                        v-text="upperCase(t('eta'))"
                    />
                    <span class="font-semibold text-yellow-500" v-text="eventGroup.eta" />
                </span>
            </template>
            <template v-if="eventGroup.driverEstimatedTimeAt">
                <span class="opacity-80" v-text="' - '" />
                <span
                    v-tooltip="t('detaExplanation')"
                    class="inline-flex items-baseline gap-1 whitespace-nowrap align-baseline"
                >
                    <span class="uppercase opacity-80 text-secondary-500">
                        {{ upperCase(t('deta')) }}
                    </span>
                    <span class="font-semibold text-secondary-500">
                        {{ localeTime(eventGroup.driverEstimatedTimeAt) }}
                    </span>
                </span>
            </template>
        </div>
        <div v-else class="text-xs font-semibold">
            {{
                eventGroup.reportEvent?.startedAt
                    ? localeTime(eventGroup.reportEvent.startedAt)
                    : t('started')
            }}

            <span
                v-if="eventGroup.stopDelayLabel"
                class="ml-2 text-xs text-yellow-500"
                v-text="eventGroup.stopDelayLabel"
            />
            <span
                v-if="eventGroup.reportEvent?.waitTime"
                class="ml-2 text-xs font-normal text-gray-600 dark:text-primary-100"
                v-text="
                    `${t('waitTime')} ${differenceFromMinutes(eventGroup.reportEvent.waitTime ?? 0)}`
                "
            />
        </div>

        <!-- Stats -->
        <div class="mt-1 space-y-0.5 text-xs whitespace-nowrap">
            <div>
                <span
                    class="font-semibold"
                    v-text="`${eventGroup.shipmentsDone}/${eventGroup.shipmentsTotal}`"
                />
                {{ t('shipments') }}
                <mdi:alert
                    v-if="
                        eventGroup.status !== TourEventStatus.Planned && eventGroup.hasUnsuccessful
                    "
                    v-tooltip="t('unsuccessfulShipmentDetected')"
                    class="inline-block ml-1 align-[-2px] text-red-400"
                />
                <mdi:alert
                    v-else-if="
                        eventGroup.status !== TourEventStatus.Planned && eventGroup.hasDeviation
                    "
                    v-tooltip="t('deviationExplanation')"
                    class="inline-block ml-1 align-[-2px] text-yellow-500"
                />
            </div>
            <div>
                <span
                    class="font-semibold"
                    v-text="
                        `${eventGroup.packagingProductsCount} / ${calculateExpectedProducts(eventGroup)}`
                    "
                />
                {{ t('packagingProducts') }}
                <mdi:alert
                    v-if="eventGroup.hasPackagingMismatch"
                    v-tooltip="t('packagingMismatchExplanation')"
                    class="inline-block ml-1 text-yellow-500 align-[-2px]"
                />
            </div>
        </div>

        <mdi:bell
            v-if="eventGroup.status === TourEventStatus.Current && eventGroup.hasDeviation"
            class="absolute right-3 bottom-1 text-red-400"
        />
    </div>
</template>
